import { useMemo } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { Checkbox } from '@carbon/react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { Date } from 'components/date';
import type { NearbySort } from './use-nearby-wells';
import type { PickerCandidate } from './types';
import { fromLineText, totalDepthText } from './well-picker-text';
import styles from './well-picker.module.scss';

/**
 * A candidate as the table holds it. `DataTable` is generic over an index signature,
 * which an interface does not satisfy on its own.
 */
type PickerRow = PickerCandidate & Record<string, unknown>;

export interface WellPickerTableProps {
  wells: PickerCandidate[];
  /** The staged UWIs, so a row can show itself already chosen. */
  stagedUwis: string[];
  /**
   * Which column the rows are ordered by, where the source can be asked to reorder
   * them. Omitted by a source that returns one fixed order, which turns sorting off
   * rather than offering a control that does nothing.
   */
  sort?: NearbySort;
  onSort?: (sort: NearbySort) => void;
  onStage: (well: PickerCandidate, checked: boolean) => void;
  /** Shown in place of rows when the source came back with nothing. */
  emptyMessage: string;
  /**
   * Whether the source is still answering. Handed to the table so it draws its own
   * skeleton — the columns are known before the rows are, so the modal keeps its shape
   * instead of collapsing and reflowing when the wells land.
   */
  loading?: boolean;
  /**
   * Where the page sits. Handed over because the table's sorting follows from it: it
   * treats sorting as the server's exactly when the paging is, which is the truth here
   * — the sweep is ranked and paged by PostGIS.
   */
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;
}

/** What a column shows where the source has no value for it. */
const MISSING = '—';

/**
 * The picker's results, in the one layout both ways in share.
 *
 * Both sources answer with wells and the same five things about them, so they get the
 * same table: what the user learns about a well does not depend on how they found it.
 * Where a source cannot know a column it reads as unknown rather than zero — the direct
 * search has no line to measure from, so "From line" is blank there.
 *
 * The app's own `DataTable` does the work: sortable headers, a sticky header over its
 * own scroll, the cells, and the skeleton it shows while the source is still answering.
 * Sorting is the server's, so it is handed over as controlled state and reported back
 * rather than done here — a table that reordered the page in front of it would put the
 * twenty-fifth well above the first while calling itself sorted. Paging is handed over
 * with it, because the table reads the two as one question: it leaves the rows alone
 * exactly when it is told the pages come from a server.
 *
 * @param {WellPickerTableProps} props - Component props.
 * @return {JSX.Element} The table.
 */
export const WellPickerTable = ({
  wells,
  stagedUwis,
  sort,
  onSort,
  onStage,
  emptyMessage,
  loading = false,
  page,
  pageSize,
  totalCount,
  onPageChange,
}: WellPickerTableProps): JSX.Element => {
  const rows = wells as PickerRow[];

  const columns = useMemo<DataTableColumnDef<PickerRow>[]>(
    () => [
      {
        // The table's own selection column labels its checkboxes with nothing, which
        // leaves a screen reader reading eleven identical checkboxes. A column of our
        // own keeps the well's name on its control, and staging is per row anyway.
        id: 'pick',
        header: '',
        enableSorting: false,
        size: 40,
        meta: { label: 'Chosen', toggleColumnFilter: false },
        cell: ({ row }) => {
          const well = row.original as PickerCandidate;
          return (
            <Checkbox
              id={`well-picker-${well.uwi}`}
              labelText={`Choose ${well.name ?? well.uwi}`}
              hideLabel
              checked={stagedUwis.includes(well.uwi)}
              onChange={(_event, { checked }) => onStage(well, checked)}
            />
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Well',
        meta: { label: 'Well' },
        cell: ({ row }) => (
          <>
            <span className={styles.wellName}>
              {row.original.name ?? 'Unnamed well'}
            </span>
            <span className={styles.wellUwi}>{row.original.uwi}</span>
          </>
        ),
      },
      {
        id: 'operator',
        accessorKey: 'operator',
        header: 'Operator',
        meta: { label: 'Operator' },
        cell: ({ row }) => row.original.operator ?? MISSING,
      },
      {
        id: 'total_depth_ft',
        accessorKey: 'totalDepthFt',
        header: 'Total depth',
        meta: { label: 'Total depth', align: 'right' },
        cell: ({ row }) => totalDepthText(row.original.totalDepthFt),
      },
      {
        id: 'spud_date',
        accessorKey: 'spudDate',
        header: 'Spud date',
        meta: { label: 'Spud date' },
        cell: ({ row }) => (
          <Date date={row.original.spudDate} fallback={MISSING} />
        ),
      },
      {
        id: 'offset_ft',
        accessorKey: 'offsetFt',
        header: 'From line',
        meta: { label: 'From line', align: 'right' },
        cell: ({ row }) => fromLineText(row.original.offsetFt),
      },
    ],
    [stagedUwis, onStage]
  );

  // The source's own ordering, shown rather than performed. Direction is the server's
  // to choose per column — distance ascends, a spud date descends — so there is nothing
  // here for a direction flag to mean.
  const sorting: SortingState = sort ? [{ id: sort, desc: false }] : [];

  // The table's own empty state is one fixed sentence for every table in the app. Which
  // source came back empty is worth saying — a radius that found nothing is a different
  // problem from a name that matches nothing — so that line stays here.
  if (!loading && wells.length === 0) {
    return <p className={styles.empty}>{emptyMessage}</p>;
  }

  return (
    <DataTable<PickerRow>
      data={rows}
      columns={columns}
      getRowId={(row, index) => String(row.uwi ?? index)}
      isLoading={loading}
      maxBodyHeight="26rem"
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      controlledState={{
        pagination: { page, pageSize },
        ...(onSort ? { sorting } : {}),
      }}
      onPaginationChange={(state) => onPageChange(state.page, state.pageSize)}
      onSortChange={(state) => {
        const next = state.sorting[0]?.id;
        if (next !== undefined) {
          onSort?.(next as NearbySort);
        }
      }}
      config={{
        // Sortable headers and our own checkbox column. The panes above own the search,
        // the radius and the eligibility switch, and the rest of the toolbar has no
        // server behind it here.
        sorting: onSort !== undefined,
        multiSort: false,
        globalSearch: false,
        columnFilters: false,
        columnVisibility: false,
        columnReordering: false,
        columnResizing: false,
        columnPinning: false,
      }}
    />
  );
};
